import { HealthProfileFieldName } from '@auracare/shared'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  SelectChangeEvent,
  OutlinedInput,
  FormHelperText,
} from '@mui/material'

const PROFILE_FIELD_LABELS: Record<HealthProfileFieldName, string> = {
  age: 'Age',
  sex: 'Sex/Gender',
  weight: 'Weight',
  height: 'Height',
  bloodPressureSystolic: 'Blood Pressure (Systolic)',
  bloodPressureDiastolic: 'Blood Pressure (Diastolic)',
  healthGoals: 'Health Goals',
  healthConcerns: 'Health Concerns',
  medications: 'Current Medications',
  supplements: 'Supplements',
  familyHistory: 'Family History',
  activeSymptoms: 'Active Symptoms',
  previousLabs: 'Previous Lab Results',
  testsOfInterest: 'Tests of Interest',
  insurance: 'Insurance Information',
  smoking: 'Smoking Status',
  focusType: 'Focus Type',
}

const ALL_PROFILE_FIELDS: HealthProfileFieldName[] = Object.keys(PROFILE_FIELD_LABELS) as HealthProfileFieldName[];

interface RequiredFieldsSelectorProps {
  value: HealthProfileFieldName[]
  onChange: (fields: HealthProfileFieldName[]) => void
}

export default function RequiredFieldsSelector({ value, onChange }: RequiredFieldsSelectorProps) {
  const handleChange = (event: SelectChangeEvent<typeof value>) => {
    const selectedValue = event.target.value
    const newValue = typeof selectedValue === 'string' ? selectedValue.split(',') as HealthProfileFieldName[] : selectedValue
    onChange(newValue)
  }

  return (
    <FormControl fullWidth>
      <InputLabel>Required Profile Fields</InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label="Required Profile Fields" />}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((field) => (
              <Chip
                key={field}
                label={PROFILE_FIELD_LABELS[field]}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        )}
      >
        {ALL_PROFILE_FIELDS.map((field) => (
          <MenuItem key={field} value={field}>
            {PROFILE_FIELD_LABELS[field]}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        Select which profile fields users must complete before generating onboarding questions.
      </FormHelperText>
    </FormControl>
  )
}
